<template>
  <Teleport to="body">
    <Transition name="fade-scale" @after-enter="focusInput">
      <div
        v-if="isOpen"
        ref="modalRef"
        class="fixed inset-0 z-9999 overflow-x-hidden overflow-y-auto bg-[#e5e5e5]"
        role="dialog"
        tabindex="0"
        aria-modal="true"
        :aria-label="t('common.close.search.modal')"
        @mousedown="handleBackdropMouseDown"
        @click="handleBackdropClick"
        @keydown.esc="emit('close')"
      >
        <button
          @click.stop="emit('close')"
          class="absolute top-5 right-5 z-50 cursor-pointer text-black md:fixed lg:top-10 lg:right-10"
          :aria-label="t('common.close.search.modal')"
        >
          <XmarkIcon class="h-4.5 w-4.5" />
        </button>

        <div
          class="mx-auto mt-25 flex min-h-screen max-w-151 flex-col px-5 pb-30 md:mt-42.5 md:w-[79%] md:max-w-195.25 md:px-0 md:pb-37.5 xl:w-[62.15%] xl:max-w-223.75 xl:pb-42.5! 2xl:pb-46!"
          @mousedown.stop
          @click.stop
        >
          <div
            :class="[
              `relative flex items-center border-b border-black pb-3.75 transition-opacity duration-200 xl:pb-5 2xl:pb-5.75`,
              {
                'opacity-45': isLoading,
              },
            ]"
          >
            <input
              ref="inputRef"
              v-model="searchQuery"
              type="text"
              :aria-label="t('common.search.modal.input')"
              class="relative z-1 w-full border-none bg-transparent p-0 pr-10 text-[26px] leading-tight text-black caret-black outline-none selection:bg-black selection:text-white md:text-4.5xl lg:text-6xl 2xl:pr-11.5 2xl:text-[70px]"
              placeholder=""
              @focus="isFocused = true"
              @blur="isFocused = false"
              @keydown.enter="handleSearch"
            />

            <div
              v-if="!searchQuery"
              class="pointer-events-none absolute inset-y-0 left-0 flex items-center text-[26px] leading-none select-none md:text-4.5xl lg:text-6xl 2xl:text-[70px]"
            >
              <span
                :class="[
                  'h-[0.85em] w-0.5 bg-black transition-opacity duration-150',
                  isFocused ? 'animate-blink opacity-100' : 'opacity-0',
                ]"
              />
              <span class="pl-2 text-gray">?</span>
            </div>

            <button
              @click="handleSearch"
              class="pointer-events-auto absolute top-1/2 right-0 -translate-y-1/2 cursor-pointer"
              :aria-label="t('common.search.modal.input')"
            >
              <LoopIcon class="h-4.75 w-4.75 text-black md:h-6.75 md:w-6.75" />
            </button>
          </div>

          <div
            v-if="!isInitialState && !isLoading && displayedItems.length === 0"
            class="pt-3.75 text-sm leading-[160%] lg:text-[17px]"
          >
            {{ texts.noResults }}
          </div>

          <div
            class="mt-20 flex flex-1 flex-col justify-between md:mt-25 xl:mt-30 2xl:mt-38"
          >
            <div
              :class="[
                'flex-1 transition-opacity duration-200',
                { 'opacity-45': isLoading },
              ]"
            >
              <div v-if="displayedItems.length > 0" class="flex flex-col">
                <div
                  class="mb-3.75 text-sm lg:text-[17px] 2xl:mb-5 2xl:text-xl/[24px]"
                >
                  {{ texts.pagesFound(totalItems) }}
                </div>

                <div class="flex flex-col" role="list">
                  <Transition name="page-fade" mode="out-in">
                    <div :key="currentPage" class="flex flex-col">
                      <NuxtLink
                        v-for="item in displayedItems"
                        :key="item.id"
                        :to="localePath(item.link)"
                        @click="emit('close')"
                        role="listitem"
                        class="group flex flex-col gap-2.5 border-t border-gray py-3.75 last:border-b 2xl:gap-3 2xl:py-4.25"
                      >
                        <h2
                          class="text-base leading-[120%] text-black transition-all duration-400 group-hover:translate-x-2.5 md:text-[17px] md:leading-[123%] lg:text-xl 2xl:text-[23px]/[29px]"
                        >
                          {{ item.title }}
                        </h2>
                        <TypeBadge :text="item.tag" />
                      </NuxtLink>
                    </div>
                  </Transition>
                </div>
              </div>
            </div>

            <div
              v-if="!isInitialState && totalPages > 1"
              :class="[
                'mt-7.5 flex shrink-0 items-center justify-center gap-5 transition-opacity duration-200 2xl:mt-8.75',
                { 'opacity-45': isLoading },
              ]"
            >
              <button
                @click="prevPage"
                :disabled="currentPage === 1"
                class="cursor-pointer text-black transition-all duration-200 hover:opacity-75 disabled:cursor-not-allowed disabled:opacity-20"
                :aria-label="t('common.prev.page')"
              >
                <ArrowIcon class="h-3.5 w-3.5 rotate-180 2xl:h-4 2xl:w-4" />
              </button>

              <span
                class="text-black select-none xl:text-[17px] 2xl:text-xl/[31px]"
              >
                {{ currentPage }} / {{ totalPages }}
              </span>

              <button
                @click="nextPage"
                :disabled="currentPage === totalPages"
                class="cursor-pointer text-black transition-all duration-200 hover:opacity-75 disabled:cursor-not-allowed disabled:opacity-20"
                :aria-label="t('common.next.page')"
              >
                <ArrowIcon class="h-3.5 w-3.5 2xl:h-4 2xl:w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import debounce from '~/utils/debounce'

interface Props {
  isOpen: boolean
}
const { locale, t } = useI18n()

const props = defineProps<Props>()
const localePath = useLocalePath()
const emit = defineEmits(['close'])

const modalRef = ref<HTMLElement | null>(null)
const inputRef = ref<HTMLInputElement | null>(null)

const isMouseDownOnBackdrop = ref(false)

const handleBackdropMouseDown = (event: MouseEvent) => {
  isMouseDownOnBackdrop.value = event.target === event.currentTarget
}

const handleBackdropClick = (event: MouseEvent) => {
  const isBackdrop = event.target === event.currentTarget
  const wasMouseDownOnBackdrop = isMouseDownOnBackdrop.value
  isMouseDownOnBackdrop.value = false

  if (!isBackdrop || !wasMouseDownOnBackdrop) return

  const selection = window.getSelection()
  if (selection && selection.toString().length > 0) return

  emit('close')
}

const searchQuery = ref('')
const isFocused = ref(false)
const currentPage = ref(1)
const ITEMS_PER_PAGE = 16

useFocusTrap(modalRef, toRef(props, 'isOpen'), () => emit('close'))

const texts = computed(() => {
  return {
    noResults: t('common.search.no_results'),
    pagesFound: (count: number) => `${count} ${t('common.search.pages_found')}`,
  }
})

const debouncedSearchQuery = ref('')
const isDebouncing = ref(false)

const searchVariables = reactive({
  query: debouncedSearchQuery,
  page: currentPage,
  first: ITEMS_PER_PAGE,
})

const { data, pending } = await usePersistedFetch('Search', searchVariables, {
  watch: [debouncedSearchQuery, currentPage],
  key: 'SearchModalQuery',
})

const isLoading = computed(() => isDebouncing.value || pending.value)

const searchData = computed(() => {
  const rawData = data.value?.search?.data || []
  return rawData.map((item: any) => ({
    ...item,
    link: item.menu_url || item.link || '#',
    tag: item.tag || item.__typename || '',
    type: item.type || item.__typename || '',
  }))
})

const totalItems = computed(() => data.value?.search?.paginatorInfo?.total || 0)
const totalPages = computed(
  () => data.value?.search?.paginatorInfo?.lastPage || 1,
)

const isInitialState = computed(() => searchQuery.value.trim() === '')

const displayedItems = computed(() => {
  if (isInitialState.value) return []
  return searchData.value
})

const performSearch = debounce(() => {
  debouncedSearchQuery.value = searchQuery.value
  isDebouncing.value = false
}, 1000)

watch(searchQuery, (newVal) => {
  currentPage.value = 1

  if (newVal.trim() === '') {
    debouncedSearchQuery.value = ''
    isDebouncing.value = false
    return
  }
  isDebouncing.value = true
  performSearch()
})

const handleSearch = () => {
  debouncedSearchQuery.value = searchQuery.value
  isDebouncing.value = false
}

const scrollToTop = () => {
  if (modalRef.value) {
    modalRef.value.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }
}

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++
    scrollToTop()
  }
}

const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--
    scrollToTop()
  }
}

const focusInput = () => {
  if (inputRef.value) {
    inputRef.value.focus()
  }
}

watch(
  () => props.isOpen,
  (newVal) => {
    if (import.meta.server) return
    if (newVal) {
      document.documentElement.style.overflow = 'hidden'
      document.body.style.overflow = 'hidden'
    } else {
      document.documentElement.style.overflow = ''
      document.body.style.overflow = ''
      searchQuery.value = ''
    }
  },
)

onUnmounted(() => {
  if (import.meta.server) return
  document.documentElement.style.overflow = ''
  document.body.style.overflow = ''
})
</script>

<style scoped>
@keyframes blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
}

.animate-blink {
  animation: blink 1s step-start infinite;
}

.fade-scale-enter-active,
.fade-scale-leave-active {
  transition:
    opacity 0.4s cubic-bezier(0.16, 1, 0.3, 1),
    transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.fade-scale-enter-from,
.fade-scale-leave-to {
  opacity: 0;
  transform: scale(0.98);
}

.page-fade-enter-active,
.page-fade-leave-active {
  transition:
    opacity 0.22s cubic-bezier(0.16, 1, 0.3, 1),
    transform 0.22s cubic-bezier(0.16, 1, 0.3, 1);
}

.page-fade-enter-from {
  opacity: 0;
  transform: translateY(4px);
}

.page-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
